import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardList, Award, TrendingUp, Loader2, Download, Printer } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentResultsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedChildData, setSelectedChildData] = useState(null);
  const [results, setResults] = useState([]);
  const [onlineExamResults, setOnlineExamResults] = useState([]);
  const [exams, setExams] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState(null);

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const response = await fetch(`${API_URL}/api/students/parent/children`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setChildren(data);
          if (data.length > 0) {
            setSelectedChild(data[0].id);
            setSelectedChildData(data[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch children:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSchoolInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/schools/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSchoolInfo(data);
        }
      } catch (error) {
        console.error('Failed to fetch school info:', error);
      }
    };

    if (token) {
      fetchChildren();
      fetchSchoolInfo();
    }
  }, [token]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!selectedChild) return;
      
      // Update selected child data
      const childData = children.find(c => c.id === selectedChild);
      setSelectedChildData(childData);
      
      try {
        // Fetch exam results for the student
        const response = await fetch(`${API_URL}/api/grades/student/${selectedChild}/results`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setResults(data.results || data || []);
        }
      } catch (error) {
        console.error('Failed to fetch results:', error);
        setResults([]);
      }
      
      try {
        // Fetch available exams
        const examsResponse = await fetch(`${API_URL}/api/exams`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (examsResponse.ok) {
          const examsData = await examsResponse.json();
          setExams(examsData || []);
        }
      } catch (error) {
        console.error('Failed to fetch exams:', error);
      }
      
      // Fetch online exam results
      try {
        const onlineExamResponse = await fetch(`${API_URL}/api/online-exam/child-results/${selectedChild}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (onlineExamResponse.ok) {
          const onlineData = await onlineExamResponse.json();
          setOnlineExamResults(onlineData || []);
        }
      } catch (error) {
        console.error('Failed to fetch online exam results:', error);
        setOnlineExamResults([]);
      }
    };

    if (selectedChild && token) {
      fetchResults();
    }
  }, [selectedChild, token, children]);

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const handlePrintResult = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Exam Results - ${selectedChildData?.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
          .header img { max-height: 80px; margin-bottom: 10px; }
          .header h1 { margin: 0; color: #1a365d; font-size: 24px; }
          .header h2 { margin: 5px 0; color: #1a365d; font-size: 18px; }
          .header p { margin: 3px 0; color: #666; font-size: 12px; }
          .result-title { text-align: center; background: #2d3748; color: white; padding: 10px; margin: 20px 0; }
          .student-info { background: #f7fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .student-info h3 { margin: 0 0 10px 0; }
          .student-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .student-info-item { margin: 5px 0; }
          .student-info-item label { color: #666; font-size: 12px; }
          .student-info-item p { margin: 2px 0; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background: #2d3748; color: white; }
          tr:nth-child(even) { background: #f7fafc; }
          .grade-A { color: #22543d; font-weight: bold; }
          .grade-B { color: #2c5282; font-weight: bold; }
          .grade-C { color: #744210; font-weight: bold; }
          .grade-F { color: #c53030; font-weight: bold; }
          .summary { margin-top: 30px; padding: 15px; background: #edf2f7; border-radius: 8px; display: flex; justify-content: space-around; }
          .summary-item { text-align: center; }
          .summary-item .value { font-size: 24px; font-weight: bold; color: #2d3748; }
          .summary-item .label { font-size: 12px; color: #666; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #666; border-top: 1px solid #ddd; padding-top: 20px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .signature-box { text-align: center; width: 150px; }
          .signature-line { border-top: 1px solid #333; margin-top: 40px; padding-top: 5px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${schoolInfo?.logo_url ? `<img src="${schoolInfo.logo_url}" alt="School Logo" />` : ''}
          <h1>${schoolInfo?.name || 'School Name'}</h1>
          <p>${schoolInfo?.address || ''}</p>
          <p>${schoolInfo?.phone ? `Phone: ${schoolInfo.phone}` : ''} ${schoolInfo?.email ? `| Email: ${schoolInfo.email}` : ''}</p>
        </div>
        
        <div class="result-title">
          <h2 style="margin:0; font-size: 16px;">EXAMINATION RESULT</h2>
          <p style="margin: 5px 0 0 0; font-size: 12px;">Academic Year 2025-26</p>
        </div>
        
        <div class="student-info">
          <div class="student-info-grid">
            <div class="student-info-item">
              <label>Student Name</label>
              <p>${selectedChildData?.name || 'Student'}</p>
            </div>
            <div class="student-info-item">
              <label>Class</label>
              <p>${selectedChildData?.class_name || 'N/A'}</p>
            </div>
            <div class="student-info-item">
              <label>Roll No / Admission No</label>
              <p>${selectedChildData?.roll_number || selectedChildData?.student_id || selectedChildData?.admission_number || 'N/A'}</p>
            </div>
            <div class="student-info-item">
              <label>Father's Name</label>
              <p>${selectedChildData?.father_name || 'N/A'}</p>
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Exam</th>
              <th>Theory</th>
              <th>Practical</th>
              <th>Total</th>
              <th>Percentage</th>
              <th>Grade</th>
            </tr>
          </thead>
          <tbody>
            ${results.map(r => `
              <tr>
                <td>${r.subject_name || r.subject || '-'}</td>
                <td>${r.exam_name || '-'}</td>
                <td style="text-align:center">${r.theory_marks ?? '-'}</td>
                <td style="text-align:center">${r.practical_marks ?? '-'}</td>
                <td style="text-align:center">${r.marks_obtained || 0}/${r.total_marks || 100}</td>
                <td style="text-align:center">${r.percentage || 0}%</td>
                <td style="text-align:center" class="grade-${(r.grade || getGrade(r.percentage || 0)).charAt(0)}">${r.grade || getGrade(r.percentage || 0)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="summary">
          <div class="summary-item">
            <div class="value">${results.length}</div>
            <div class="label">Total Subjects</div>
          </div>
          <div class="summary-item">
            <div class="value">${results.reduce((sum, r) => sum + (r.marks_obtained || 0), 0)}/${results.reduce((sum, r) => sum + (r.total_marks || 100), 0)}</div>
            <div class="label">Total Marks</div>
          </div>
          <div class="summary-item">
            <div class="value">${results.length > 0 ? (results.reduce((sum, r) => sum + (r.percentage || 0), 0) / results.length).toFixed(1) : 0}%</div>
            <div class="label">Average</div>
          </div>
        </div>

        <div class="signatures">
          <div class="signature-box">
            <div class="signature-line">Class Teacher</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Exam Controller</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Principal</div>
          </div>
        </div>

        <div class="footer">
          <p>This is a computer generated result sheet</p>
          <p>Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDownloadResult = () => {
    // Create CSV content
    let csvContent = "Subject,Exam,Theory,Practical,Total,Max Marks,Percentage,Grade\n";
    results.forEach(r => {
      csvContent += `"${r.subject_name || r.subject || '-'}","${r.exam_name || '-'}",${r.theory_marks ?? '-'},${r.practical_marks ?? '-'},${r.marks_obtained || 0},${r.total_marks || 100},${r.percentage || 0}%,${r.grade || getGrade(r.percentage || 0)}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Results_${selectedChildData?.name || 'Student'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Result downloaded successfully!');
  };

  if (loading) {
    return (
      <DashboardLayout title="Child Results">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Child Results">
      <div className="space-y-6">
        {/* Child Selector */}
        {children.length > 1 && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">Select Child:</label>
                <Select value={selectedChild} onValueChange={setSelectedChild}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(child => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Child Info */}
        {selectedChildData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{selectedChildData.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Class: {selectedChildData.class_name || 'N/A'} | Roll: {selectedChildData.roll_number || 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Results */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Exam Results
              </CardTitle>
              {results.length > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleDownloadResult}>
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV
                  </Button>
                  <Button variant="default" size="sm" onClick={handlePrintResult}>
                    <Printer className="h-4 w-4 mr-2" />
                    Print Result
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {results.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No exam results available yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Results will appear here once exams are conducted and graded
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map((result, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="border rounded-lg p-4"
                    data-testid={`result-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold">{result.subject_name || result.subject || 'Subject'}</h4>
                        <p className="text-xs text-muted-foreground">{result.exam_name || 'Exam'}</p>
                      </div>
                      <span className={`text-2xl font-bold ${getGradeColor(result.percentage || 0)}`}>
                        {result.grade || getGrade(result.percentage || 0)}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Theory</p>
                        <p className="font-medium">{result.theory_marks ?? '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Practical</p>
                        <p className="font-medium">{result.practical_marks ?? '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total</p>
                        <p className="font-medium">{result.marks_obtained || 0}/{result.total_marks || 100}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Percentage</p>
                        <p className={`font-medium ${getGradeColor(result.percentage || 0)}`}>
                          {result.percentage || 0}%
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Online Exam Results Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Online Exam Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {onlineExamResults.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No online exam results yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Online quiz and exam results will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {onlineExamResults.map((result, idx) => (
                  <motion.div 
                    key={result.attempt_id || idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">{result.exam_title}</h4>
                        <p className="text-xs text-muted-foreground">
                          {result.submitted_at ? new Date(result.submitted_at).toLocaleDateString('en-IN') : '-'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xl font-bold ${result.is_passed ? 'text-green-600' : 'text-red-600'}`}>
                          {result.obtained_marks}/{result.total_marks}
                        </span>
                        <p className={`text-sm ${result.is_passed ? 'text-green-600' : 'text-red-600'}`}>
                          {result.percentage?.toFixed(1)}% - {result.is_passed ? 'PASS' : 'FAIL'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ParentResultsPage;
